import { m } from "framer-motion";
import { useState } from "react";
import {
  Alert02Icon,
  Cancel01Icon,
  CentralIcon,
  CheckmarkCircle02Icon,
  InformationCircleIcon,
  OctagonXIcon,
} from "~/components/icons/central-icons";
import { Button } from "~/components/ui/button";
import { fluidSprings } from "~/components/ui/fluid-motion";
import { ProgressRing } from "~/components/ui/progress-ring";
import { m as messages } from "~/lib/i18n";
import { cn } from "~/lib/utils";
import type { SimpleToastRecord } from "../simple-toast-store";
import {
  TOAST_PILL_ENTER_FROM,
  TOAST_PILL_EXIT,
  TOAST_PILL_LAYOUT_TRANSITION,
  TOAST_PILL_RISE,
} from "./pill-motion";

const KIND_TINTS: Record<SimpleToastRecord["kind"], string> = {
  success:
    "color-mix(in oklab, var(--brand-green-readable) 24%, transparent)",
  error: "color-mix(in oklab, var(--destructive) 20%, transparent)",
  warning: "color-mix(in oklab, var(--glider-highlight) 22%, transparent)",
  info: "transparent",
  loading:
    "color-mix(in oklab, var(--brand-green-readable) 16%, transparent)",
};

function KindIcon({ kind }: { kind: SimpleToastRecord["kind"] }) {
  if (kind === "loading") {
    return (
      <span className="block text-status-positive-foreground">
        <ProgressRing size={20} strokeWidth={2} />
      </span>
    );
  }

  const icon =
    kind === "success"
      ? CheckmarkCircle02Icon
      : kind === "error"
        ? OctagonXIcon
        : kind === "warning"
          ? Alert02Icon
          : InformationCircleIcon;

  return (
    <CentralIcon
      className={cn(
        "size-5",
        kind === "success" && "text-status-positive-foreground",
        kind === "error" && "text-status-negative-foreground",
        kind === "warning" && "text-status-warning-foreground",
        kind === "info" && "text-muted-foreground",
      )}
      icon={icon}
    />
  );
}

interface SimpleToastPillProps {
  isFrontmost: boolean;
  isStackHovered: boolean;
  onDismiss: () => void;
  record: SimpleToastRecord;
  stackTotal: number;
}

/**
 * Non-execution pill in the center stack (v1's SimplePillNotification):
 * kind icon, message, optional description, hover dismiss badge.
 */
export function SimpleToastPill({
  isFrontmost,
  isStackHovered,
  onDismiss,
  record,
  stackTotal,
}: SimpleToastPillProps) {
  const [isPillHovered, setIsPillHovered] = useState(false);
  const [isDismissFocused, setIsDismissFocused] = useState(false);
  const revealDismiss = isPillHovered || isDismissFocused;

  return (
    <m.div
      animate={{ opacity: 1, y: 0 }}
      className="relative"
      data-kind={record.kind}
      data-testid="simple-toast-pill"
      exit={TOAST_PILL_EXIT}
      initial={TOAST_PILL_ENTER_FROM}
      layout
      onHoverEnd={() => setIsPillHovered(false)}
      onHoverStart={() => setIsPillHovered(true)}
      transition={{
        ...TOAST_PILL_RISE,
        layout: TOAST_PILL_LAYOUT_TRANSITION,
      }}
    >
      <div className="relative overflow-hidden rounded-full bg-surface-4 shadow-surface-4">
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            background: `radial-gradient(135% 135% at 50% -102%, ${KIND_TINTS[record.kind]} 0%, transparent 100%)`,
          }}
        />

        <div className="relative flex items-center gap-2.5 px-4 py-2.5">
          <div className="shrink-0">
            <KindIcon kind={record.kind} />
          </div>
          <div className="min-w-0">
            <p className="text-meta font-medium whitespace-nowrap text-foreground">
              {record.message}
            </p>
            {record.description ? (
              <div className="max-w-72 truncate text-caption text-muted-foreground">
                {record.description}
              </div>
            ) : null}
          </div>
          {record.action ? (
            <Button
              className="ml-1"
              onClick={record.action.onSelect}
              size="xs"
              type="button"
            >
              {record.action.label}
            </Button>
          ) : null}
        </div>
      </div>

      {isFrontmost && stackTotal > 1 && !isStackHovered ? (
        <div className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-foreground shadow-lg">
          <span className="text-caption font-semibold text-background">
            +{stackTotal - 1}
          </span>
        </div>
      ) : null}

      <m.button
        animate={{
          opacity: revealDismiss ? 1 : 0,
          scale: revealDismiss ? 1 : 0.75,
        }}
        aria-label={messages.toast_simple_pill_dismiss_aria_label()}
        className="absolute -top-1 -right-1 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-foreground shadow-lg glider-focus-ring"
        data-testid="simple-toast-dismiss"
        initial={false}
        onBlur={() => setIsDismissFocused(false)}
        onClick={onDismiss}
        onFocus={() => setIsDismissFocused(true)}
        style={{ pointerEvents: revealDismiss ? "auto" : "none" }}
        transition={fluidSprings.fast}
        type="button"
      >
        <CentralIcon className="size-3 text-background" icon={Cancel01Icon} />
      </m.button>
    </m.div>
  );
}
